import { Line } from '@visx/shape';
import { inRange } from 'lodash-es';

import Colors from '@dynatrace/strato-design-tokens/colors';

import { LeftMagnifier } from './components/LeftMagnifier.js';
import { RightMagnifier } from './components/RightMagnifier.js';
import { usePoliteContainer } from '../../../../overlays/hooks/usePoliteContainer.js';

interface HorizontalCrosshairProps {
  /** Coordinate y for crosshair */
  y: number;
  /** Left label for crosshair */
  leftLabel?: string;
  /** Right label for the crosshair */
  rightLabel?: string;
  /** Width for crosshair line */
  width: number;
  /** Height for crosshair line */
  height?: number;
  /** Top margin for crosshair line */
  top?: number;
  /** Left offset */
  leftOffset?: number;
  /** Right offset */
  rightOffset?: number;
  /** Indicates wether the line needs to be dashed */
  dashed?: boolean;
  /** Chart bounds to calculate magnifier position */
  chartBounds?: DOMRect;
}

export const HorizontalCrosshair = (props: HorizontalCrosshairProps) => {
  const {
    y,
    leftLabel,
    rightLabel,
    width,
    height = Infinity,
    top = 0,
    leftOffset = 0,
    rightOffset = 0,
    dashed = true,
    chartBounds,
  } = props;

  const lineStrokeDasharray = dashed ? '3,2' : '0,0';

  const Portal = usePoliteContainer();

  const from = { x: -leftOffset, y: top + y };
  const to = { x: width - leftOffset, y: top + y };
  const strokeWidth = 1;
  const isInRange = inRange(y, 0, height + 1);

  return (
    <>
      {isInRange && (
        <Line
          data-testid="crosshair"
          from={from}
          to={to}
          stroke={Colors.Border.Neutral.Accent}
          strokeWidth={strokeWidth}
          strokeDasharray={lineStrokeDasharray}
          pointerEvents={'none'}
        />
      )}
      {leftLabel && (
        <Portal>
          <LeftMagnifier
            label={leftLabel}
            mouseY={y}
            offset={leftOffset}
            chartBounds={chartBounds}
          />
        </Portal>
      )}
      {rightLabel && (
        <Portal>
          <RightMagnifier
            label={rightLabel}
            mouseY={y}
            offset={rightOffset}
            chartBounds={chartBounds}
          />
        </Portal>
      )}
    </>
  );
};

HorizontalCrosshair['displayName'] = 'HorizontalCrosshair';
